import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError, createClient } from '@connectrpc/connect';
import { RegistryRecordService } from '../../../../../../gen/ts/mono/datagateway/v1/registry_records_connect';
import { connectTransport } from '../../../lib/connect/transport';
import { AudienceConflictError } from './intelClaimTypes';
/**
 * The hearsay store: REG-02's RegistryRecordService (pangaea, via sabaody).
 * Tenant and author come from the session; the server decides who sees each
 * claim (author, or hearsay.read ceiling AND the claim's audience), so this
 * client only maps shapes.
 */ export const registryRecordsClient = createClient(RegistryRecordService, connectTransport);
const toDate = (ts)=>ts ? ts.toDate() : null;
/** 'YYYY-MM-DD' (SpecraDateField) → a UTC-midnight Timestamp; '' → unset. */ export function dayToTimestamp(day) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
    return Timestamp.fromDate(new Date(`${day}T00:00:00Z`));
}
function toTarget(t) {
    const kind = t.kind;
    const fallback = kind === 'USER' ? 'Removed person' : 'Removed group';
    return {
        kind,
        id: t.id,
        label: t.displayName || fallback
    };
}
function toAudience(a) {
    return {
        audienceId: a?.audienceId ?? '',
        level: a?.level || 'PERSONAL',
        summary: a?.summary ?? '',
        targets: (a?.targets ?? []).map(toTarget),
        changeCount: a?.changeCount ?? 1,
        changedByName: a?.changedByName ?? '',
        changedAt: toDate(a?.changedAt)
    };
}
/** Map the wire claim; "heard from"/"where" prefer the resolved link over fallback text. */ export function fromProtoClaim(c) {
    const links = c.links.map((l)=>({
            linkRole: l.linkRole,
            targetKind: l.targetKind,
            targetId: l.resolvedTargetId || l.targetId,
            label: l.name
        }));
    return {
        id: c.id,
        kind: c.claimKind,
        source: c.sourceType,
        statement: c.statement,
        heardFromName: c.heardFrom?.name || c.heardFromText,
        venue: c.heardAt?.name || c.venueText,
        heardOn: toDate(c.heardOn),
        confidence: c.confidence,
        verification: c.verification,
        expiresOn: toDate(c.expiresOn),
        recordedByName: c.recordedByName,
        viewerIsAuthor: c.viewerIsAuthor,
        audience: toAudience(c.audience),
        suggestedTeam: c.suggestedTeam?.kind ? toTarget(c.suggestedTeam) : {
            kind: '',
            id: '',
            label: ''
        },
        links
    };
}
const toAudienceInput = (a)=>({
        everyone: a.everyone,
        targets: a.everyone ? [] : a.targets.map((t)=>({
                kind: t.kind,
                id: t.id
            }))
    });
/** Connect Aborted on SetIntelClaimAudience = someone changed it first. */ function rethrowConflict(err) {
    if (err instanceof ConnectError && err.code === Code.Aborted) throw new AudienceConflictError();
    throw err;
}
export function createIntelClaimsClient(rpc = registryRecordsClient) {
    return {
        async listIntelClaims (subject) {
            const res = await rpc.listIntelClaims({
                subjectKind: subject.kind,
                subjectId: subject.id
            });
            return {
                claims: res.claims.map(fromProtoClaim),
                canCreate: res.canCreate,
                canCreateKinds: [
                    ...res.canCreateKinds
                ]
            };
        },
        async listIntelClaimsForLinks (links) {
            const res = await rpc.listIntelClaims({
                links: links.map((l)=>({
                        linkRole: l.linkRole,
                        targetKind: l.targetKind,
                        targetId: l.targetId
                    }))
            });
            return {
                claims: res.claims.map(fromProtoClaim),
                canCreate: res.canCreate,
                canCreateKinds: [
                    ...res.canCreateKinds
                ]
            };
        },
        async createIntelClaim (input) {
            const res = await rpc.createIntelClaim({
                id: crypto.randomUUID(),
                statement: input.statement,
                claimKind: input.kind,
                sourceType: input.source,
                heardFromContactId: input.heardFromContactId,
                heardFromText: input.heardFromText,
                venueText: input.venue,
                heardOn: dayToTimestamp(input.heardOn),
                confidence: input.confidence,
                expiresOn: dayToTimestamp(input.expiresOn),
                sourceArtifactId: input.documentArtifactId,
                links: input.links.map((l)=>({
                        linkRole: l.linkRole,
                        targetKind: l.targetKind,
                        targetId: l.targetId
                    })),
                audience: input.audience ? toAudienceInput(input.audience) : undefined
            });
            if (!res.claim) throw new Error('The server did not return the saved claim.');
            return fromProtoClaim(res.claim);
        },
        async updateIntelClaimVerification (input) {
            const res = await rpc.updateIntelClaimVerification({
                claimId: input.claimId,
                verification: input.verification,
                note: input.note,
                corroboratedByArtifactId: input.artifactId
            });
            if (!res.claim) throw new Error('The server did not return the claim.');
            return fromProtoClaim(res.claim);
        },
        async retractIntelClaim (input) {
            await rpc.retractIntelClaim({
                claimId: input.claimId,
                reason: input.reason
            });
        },
        async setIntelClaimAudience (input) {
            try {
                const res = await rpc.setIntelClaimAudience({
                    claimId: input.claimId,
                    audience: toAudienceInput(input.audience),
                    expectedAudienceId: input.expectedAudienceId
                });
                if (!res.claim) throw new Error('The server did not return the claim.');
                return fromProtoClaim(res.claim);
            } catch (err) {
                return rethrowConflict(err);
            }
        },
        async listIntelClaimAudienceHistory (claimId) {
            const res = await rpc.listIntelClaimAudienceHistory({
                claimId
            });
            return res.changes.map((c)=>({
                    audienceId: c.audienceId,
                    summary: c.summary,
                    changedAt: toDate(c.changedAt),
                    changedByName: c.changedByName,
                    note: c.note,
                    direction: c.direction || 'CHANGED'
                }));
        }
    };
}
export const intelClaimsClient = createIntelClaimsClient();
